import { useEffect, useState } from "react";
import { AtSymbolIcon } from "@heroicons/react/24/outline";
import { PhoneIcon } from "@heroicons/react/24/solid";
import { fetchEmployees } from "../../api/users";
import { PageHeading } from "../../components/utils/pageHeading";
import { ContactCTA } from "../../components/home/ContactCTA";

function getInitials(name) {
  if (!name) return "AA";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "AA";
  const first = parts[0][0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase();
}

export const AboutEmployees = () => {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const res = await fetchEmployees({
          per_page: 100,
          fields: "id,displayTitle,name,email,mobile_work,working_posistion",
        });
        const list = Array.isArray(res?.data) ? res.data : [];
        const normalizeId = (x) => {
          const n = Number(x);
          return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
        };
        const sorted = [...list].sort(
          (a, b) => normalizeId(a?.id) - normalizeId(b?.id)
        );
        setItems(sorted);
      } catch (e) {
        setError("Kunne ikke hente ansatte nå.");
      } finally {
        setIsLoading(false);
      }
    }
    load();
  }, []);

  return (
    <>
      <PageHeading
        eyebrow="03 / Teamet"
        title="Et voksende team som kan faget sitt."
        description="Bli kjent med menneskene som står bak hvert prosjekt, hver serviceavtale og hver filterbytting."
      />

      <section className="sec" style={{ borderTop: "none" }}>
        <div className="wrap">
          <div className="sec-head">
            <div className="sec-tag">Våre ansatte</div>
            <h2>
              Folk <em>som står bak løsningene.</em>
            </h2>
            <div className="source-badge">
              <span className="pulse" />
              <span>{items.length > 0 ? `${items.length} ansatte · synk fra Promang` : "synk fra Promang"}</span>
            </div>
          </div>

          {isLoading ? (
            <div className="team-grid">
              {Array.from({ length: 6 }).map((_, idx) => (
                <div className="member" key={idx} style={{ opacity: 0.6 }}>
                  <div className="ph">
                    <span className="initials">··</span>
                  </div>
                  <div className="info">
                    <span className="name" style={{ background: "var(--bg-2)", height: 14, borderRadius: 6 }} />
                    <span className="role" style={{ background: "var(--bg-2)", height: 10, borderRadius: 6, width: "60%" }} />
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div
              style={{
                padding: "32px",
                borderRadius: 16,
                border: "1px solid rgba(220, 38, 38, 0.25)",
                background: "rgba(220, 38, 38, 0.05)",
                color: "var(--ink-2)",
                textAlign: "center",
              }}
            >
              {error}
            </div>
          ) : items.length === 0 ? (
            <div
              style={{
                padding: "32px",
                borderRadius: 16,
                border: "1px solid var(--line)",
                background: "#fff",
                color: "var(--muted)",
                textAlign: "center",
              }}
            >
              Ingen ansatte å vise akkurat nå.
            </div>
          ) : (
            <div className="team-grid">
              {items.map((person) => {
                const name = person?.displayTitle || person?.name;
                const role =
                  person?.working_posistion || person?.department?.name || "Airon AS";
                const email = person?.email;
                const mobile = person?.mobile_work || person?.mobile;
                const imageUrl = person?.image_url || person?.avatar_url || null;

                return (
                  <div className="member" key={person?.id || name}>
                    <div className="ph">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={name || "Ansatt"}
                          style={{
                            position: "absolute",
                            inset: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      ) : (
                        <span className="initials">{getInitials(name)}</span>
                      )}
                    </div>
                    <div className="info">
                      <span className="name">{name || "Ansatt"}</span>
                      <span className="role">{role}</span>
                      <span className="contact" style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                        {email ? (
                          <a href={`mailto:${email}`} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                            <AtSymbolIcon style={{ width: 14, height: 14 }} />
                            {email}
                          </a>
                        ) : null}
                        {mobile ? (
                          <a href={`tel:${mobile}`} style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--muted)" }}>
                            <PhoneIcon style={{ width: 12, height: 12 }} />
                            {mobile}
                          </a>
                        ) : null}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

        </div>
      </section>

      <ContactCTA
        title="Vil du snakke med noen hos oss?"
        highlight="Ring, mail eller bestill befaring."
        description="Teamet er lite og tilgjengelig — ta kontakt med den som passer ditt prosjekt."
      />
    </>
  );
};
